import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { AttendanceAction } from "@/features/sessions/constants/attendanceStatus";
import { sessionsKeys } from "@/features/sessions/queries/sessionsQueryKeys";
import attendanceService from "@/features/sessions/services/attendanceService";
import bookingService from "@/features/sessions/services/bookingService";
import type { AttendanceWithUser } from "@/features/sessions/types";

interface MarkAttendanceVariables {
    attendanceId: string;
    sessionId: string;
    action: AttendanceAction;
}

type MarkAttendanceContext = {
    previous: AttendanceWithUser[] | undefined;
};

export type UseMarkAttendanceOptions = {
    /** Called after a successful mark (e.g. exit edit mode on the review row). */
    onMarked?: () => void;
};

function applyActionToAttendance(record: AttendanceWithUser, action: AttendanceAction): AttendanceWithUser {
    const now = new Date();
    if (action === "attended") {
        return {
            ...record,
            attendedAt: now,
            cancelledAt: undefined,
            resolvedAt: now,
            wasAbsent: false,
        };
    }
    if (action === "absent") {
        return {
            ...record,
            attendedAt: undefined,
            resolvedAt: now,
            tableNumber: null,
            wasAbsent: true,
        };
    }
    return {
        ...record,
        attendedAt: undefined,
        cancelledAt: now,
        resolvedAt: now,
        tableNumber: null,
        wasAbsent: false,
    };
}

/**
 * Mutation hook for marking attendance (attended, absent, or cancelled)
 * Includes optimistic list updates, query invalidation, and optional `onMarked` (e.g. clear edit mode).
 */
export function useMarkAttendance(options?: UseMarkAttendanceOptions) {
    const queryClient = useQueryClient();
    const onMarked = options?.onMarked;

    const mutation = useMutation<unknown, Error, MarkAttendanceVariables, MarkAttendanceContext>({
        mutationFn: async ({ attendanceId, sessionId, action }) => {
            switch (action) {
                case "attended":
                    return attendanceService.markAttendanceAsAttended(attendanceId);
                case "absent":
                    return attendanceService.markAttendanceAsAbsent(attendanceId);
                case "cancelled":
                    return bookingService.adminCancelBooking({ attendanceId, sessionId });
            }
        },
        onError: (error, variables, context) => {
            if (context?.previous) {
                queryClient.setQueryData(sessionsKeys.attendance.session(variables.sessionId), context.previous);
            }
            const errorMessage =
                error instanceof Error ? error.message : "Failed to update attendance. Please try again.";
            toast.error(errorMessage);
        },
        onMutate: async (variables) => {
            const { attendanceId, sessionId, action } = variables;
            await queryClient.cancelQueries({ queryKey: sessionsKeys.attendance.session(sessionId) });
            const previous = queryClient.getQueryData<AttendanceWithUser[]>(sessionsKeys.attendance.session(sessionId));
            if (previous) {
                const next = previous.map((a) => (a.id === attendanceId ? applyActionToAttendance(a, action) : a));
                queryClient.setQueryData(sessionsKeys.attendance.session(sessionId), next);
            }
            return { previous };
        },
        onSuccess: (_data, variables) => {
            onMarked?.();
            queryClient.invalidateQueries({
                queryKey: sessionsKeys.attendance.session(variables.sessionId),
            });
            queryClient.invalidateQueries({
                queryKey: [...sessionsKeys.attendance.session(variables.sessionId), "status-counts"],
            });
            if (variables.action === "cancelled") {
                queryClient.invalidateQueries({
                    queryKey: sessionsKeys.sessions.detail(variables.sessionId),
                });
            }
        },
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isPending: mutation.isPending,
        isSuccess: mutation.isSuccess,
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
    };
}
